// Tenant integrations (/v1/admin/integrations*) and the Overview's per-member list.

import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import type { ProviderHealth, ProviderTestResult, UserRef } from "./types";

export type IntegrationKind = "odoo";

/** GET /v1/admin/integrations item. */
export interface IntegrationView {
  kind: IntegrationKind;
  name: string;
  configured: boolean;
  url?: string;
  database?: string;
  health: ProviderHealth;
  users_connected: number;
  users_attention: number;
  updated_at?: string;
  mode?: import("./types").IntegrationMode;
  changed?: import("./types").ChangeInfo | null;
}

export interface OdooTenantSettings {
  url: string;
  database: string;
}

export type UserIntegrationState = "connected" | "needs_reconnect" | "error" | "not_connected";

/** One member's CRM connection on the Overview. */
export interface UserIntegration {
  user: UserRef;
  kind: IntegrationKind;
  state: UserIntegrationState;
  error?: string;
  error_at?: string;
  last_ok_at?: string;
  reminded_at?: string;
}

export interface ReminderResult {
  status: "queued" | "not_configured";
  reminded_at: string;
}

export const integrationsApi = {
  list: () => api<IntegrationView[]>("/v1/admin/integrations"),
  save: (kind: IntegrationKind, body: OdooTenantSettings) =>
    api<IntegrationView>(`/v1/admin/integrations/${kind}`, { method: "PUT", body }),
  remove: (kind: IntegrationKind) =>
    api<IntegrationView>(`/v1/admin/integrations/${kind}`, { method: "DELETE" }),
  test: (kind: IntegrationKind, body?: OdooTenantSettings) =>
    api<ProviderTestResult>(`/v1/admin/integrations/${kind}/test`, {
      method: "POST",
      ...(body ? { body } : {}),
    }),
  /** "Request an integration": filed as a problem report, the existing feedback flow. The `id`
   * makes a retried send safe. */
  request: (text: string, id: string = crypto.randomUUID()) => {
    const form = new FormData();
    form.set("id", id);
    form.set("note", `Integration request: ${text.trim()}`);
    form.set("diagnostics", JSON.stringify({ source: "console", kind: "integration_request" }));
    return api<{ id: string; created: boolean }>("/v1/bug-reports", { method: "POST", form });
  },
  remind: (userId: string) =>
    api<ReminderResult>(`/v1/admin/integrations/users/${encodeURIComponent(userId)}/remind`, {
      method: "POST",
    }),
};

export const integrationsKey = ["admin", "integrations"] as const;

export const integrationsQuery = queryOptions({
  queryKey: integrationsKey,
  queryFn: integrationsApi.list,
});

export const INTEGRATION_LABEL: Record<IntegrationKind, string> = { odoo: "Odoo" };

export const USER_INTEGRATION_STATE: Record<
  UserIntegrationState,
  { label: string; tone: "ok" | "warn" | "danger" | "neutral" }
> = {
  connected: { label: "Connected", tone: "ok" },
  needs_reconnect: { label: "Needs reconnect", tone: "warn" },
  error: { label: "Error", tone: "danger" },
  not_connected: { label: "Not connected", tone: "neutral" },
};

/** "5 min ago", "just now" or "on Oct 12", to follow a verb ("Last worked ..."). */
export function since(iso: string, now = Date.now()): string {
  const diff = Math.round((now - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `on ${new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}
